/**
 * Shared Motion Detail controller — Golden Test implementation.
 * Used by both the Hover Reference Test and the Production Work section.
 * DO NOT modify geometry, duration, ease, or curviness without a Test-first validation pass.
 */
import { gsap } from 'gsap';
import { MotionPathPlugin } from 'gsap/MotionPathPlugin';

gsap.registerPlugin(MotionPathPlugin);

/* Codrops Demo 2 parameters — preserved. */
export const MOTION_DETAIL_DURATION = 1.2;
export const MOTION_DETAIL_EASE = 'expo.inOut';
export const MOTION_DETAIL_CURVINESS = 0.45;

/* Fixed Filmstrip target (not a % of the Work card). */
export const THUMB_WIDTH = 80;
export const THUMB_GAP = 10;

export type DetailState = 'idle' | 'opening' | 'open' | 'closing';

export interface MotionDetailElements {
  stage: HTMLElement;
  backdrop: HTMLElement;
  featured: HTMLElement;
  filmstripAnchor: HTMLElement;
  /** Always-current list of thumbnail wrappers. */
  getThumbs: () => HTMLElement[];
  onOpenStart?: () => void;
  onReverseComplete: () => void;
}

export function createMotionDetailController(els: MotionDetailElements) {
  let tl: gsap.core.Timeline | null = null;
  let source: HTMLElement | null = null;
  let state: DetailState = 'idle';
  let resizeTimer: number | null = null;
  let cleanupRaf1 = 0;
  let cleanupRaf2 = 0;

  const cancelCleanup = () => {
    if (cleanupRaf1) cancelAnimationFrame(cleanupRaf1);
    if (cleanupRaf2) cancelAnimationFrame(cleanupRaf2);
    cleanupRaf1 = 0;
    cleanupRaf2 = 0;
  };

  const hideStage = () => {
    gsap.set(els.stage, { visibility: 'hidden', pointerEvents: 'none' });
    els.stage.setAttribute('aria-hidden', 'true');
  };
  const showStage = () => {
    gsap.set(els.stage, { visibility: 'visible', pointerEvents: 'auto' });
    els.stage.setAttribute('aria-hidden', 'false');
  };

  const build = () => {
    if (!source) return;
    tl?.kill();
    const thumbs = els.getThumbs();
    const rect = source.getBoundingClientRect();
    const anchor = els.filmstripAnchor.getBoundingClientRect();
    const scale = THUMB_WIDTH / rect.width;
    const itemH = rect.height * scale;
    const total = thumbs.length * THUMB_WIDTH + (thumbs.length - 1) * THUMB_GAP;
    const startLeft = anchor.left + anchor.width / 2 - total / 2;

    gsap.set(thumbs, {
      position: 'fixed',
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
      x: 0,
      y: 0,
      scale: 1,
      transformOrigin: 'top left',
      opacity: 1,
      force3D: true,
      zIndex: (i: number) => thumbs.length - i + 20,
    });
    gsap.set(els.backdrop, { opacity: 0 });
    gsap.set(els.featured, { opacity: 0, scale: 0.985 });
    showStage();

    tl = gsap.timeline({
      paused: true,
      defaults: { duration: MOTION_DETAIL_DURATION, ease: MOTION_DETAIL_EASE },
      onComplete: () => { state = 'open'; },
      onReverseComplete: () => {
        tl?.pause();
        // 1. Hide stage — the only work on the final visible frame.
        hideStage();
        source = null;
        state = 'idle';
        if (resizeTimer) { window.clearTimeout(resizeTimer); resizeTimer = null; }
        cancelCleanup();
        // 2. Next frame (stage invisible): clear animation styles.
        const capturedThumbs = thumbs.slice();
        cleanupRaf1 = requestAnimationFrame(() => {
          cleanupRaf1 = 0;
          gsap.set(capturedThumbs, { clearProps: 'position,top,left,width,height,x,y,scale,transform,opacity,zIndex' });
          gsap.set(els.featured, { clearProps: 'transform,opacity' });
          // 3. Following frame: unlock / reset Work.
          cleanupRaf2 = requestAnimationFrame(() => {
            cleanupRaf2 = 0;
            els.onReverseComplete();
          });
        });
      },
    });

    tl.to(
      thumbs,
      {
        motionPath: (i: number) => {
          const dx = startLeft + i * (THUMB_WIDTH + THUMB_GAP) - rect.left;
          const dy = anchor.bottom - itemH - rect.top;
          if (i === 0) return { path: [{ x: dx, y: dy, scale }] };
          return {
            path: [
              { x: dx * 0.08 + THUMB_GAP, y: dy * 0.45, scale: (1 - scale) * 0.5 + scale },
              { x: dx * 0.35 + i * 0.45 + THUMB_GAP, y: dy * 0.95, scale: (1 - scale) * 0.25 + scale },
              { x: dx, y: dy, scale },
            ],
            curviness: MOTION_DETAIL_CURVINESS,
          };
        },
        stagger: { from: 'end', each: 0.02 },
        force3D: true,
      } as gsap.TweenVars,
      0,
    );
    tl.to(els.featured, { opacity: 1, scale: 1 }, 0);
    tl.to(els.backdrop, { opacity: 1, duration: 0.45, ease: 'power2.out' }, 0);
  };

  const open = (src: HTMLElement) => {
    cancelCleanup();
    source = src;
    state = 'opening';
    build();
    els.onOpenStart?.();
    tl?.play(0);
  };

  /** Reverse the SAME timeline — valid during opening too. */
  const close = () => {
    if (!tl || state === 'idle') return;
    state = 'closing';
    tl.reverse();
  };

  const onResize = () => {
    if (state === 'idle' || state === 'closing' || !source || !tl) return;
    if (resizeTimer) window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      resizeTimer = null;
      if (state === 'idle' || state === 'closing' || !source || !tl) return;
      const p = tl.progress();
      const reversed = tl.reversed();
      tl.progress(0).pause();
      build();
      tl?.progress(p);
      if (reversed) tl?.reverse(); else if (state === 'opening') tl?.play();
    }, 120);
  };

  window.addEventListener('resize', onResize);
  hideStage();

  return {
    open,
    close,
    isActive: () => state !== 'idle',
    isClosing: () => state === 'closing',
    destroy() {
      tl?.kill();
      tl = null;
      if (resizeTimer) window.clearTimeout(resizeTimer);
      cancelCleanup();
      window.removeEventListener('resize', onResize);
    },
  };
}

export type MotionDetailController = ReturnType<typeof createMotionDetailController>;